import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useFieldSalesHqProductivity } from '../hooks/useFieldSalesDashboard.js';
import {
  formatValueAuto,
  formatIndianInt,
  achDelta,
  deltaClass,
  DELTA_COLOR,
  FS_HEAD_SX,
  FS_CELL_SX,
} from './fieldSalesFormat.js';
import FieldSalesPager from './FieldSalesPager.jsx';
import { usePagedRows } from './fieldSalesPaging.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const PRIMARY = '#2E7CF6';
const BAD = '#DC2626';
const WARN = '#B45309';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';

const N_OPTIONS = [10, 20, 30, 50, 100];

// Thin top-line accent per summary card (reference #hqSummary .mini:before).
const CARD_ACCENTS = [
  'linear-gradient(90deg,#2E7CF6,#1657c8)',
  'linear-gradient(90deg,#16A34A,#0e7a37)',
  'linear-gradient(90deg,#7C3AED,#5b21b6)',
  'linear-gradient(90deg,#F59E0B,#d97706)',
];

// PCPM/TM pill tone from the vs-division index (reference idxClass).
const PILL = {
  good: { bg: 'rgba(22,163,74,.12)', color: GOOD, border: 'rgba(22,163,74,.3)' },
  mid: { bg: 'rgba(46,124,246,.12)', color: PRIMARY, border: 'rgba(46,124,246,.3)' },
  warn: { bg: 'rgba(245,158,11,.16)', color: WARN, border: 'rgba(245,158,11,.35)' },
  bad: { bg: 'rgba(220,38,38,.12)', color: BAD, border: 'rgba(220,38,38,.3)' },
};
function idxTone(idx) {
  const v = Number(idx);
  if (!Number.isFinite(v)) return 'mid';
  if (v > 150) return 'good';
  if (v >= 100) return 'mid';
  if (v >= 50) return 'warn';
  return 'bad';
}

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** A summary mini-card with a thin coloured top line (reference #hqSummary). */
function SummaryCard({ accent, label, value, sub }) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #e3ebf7',
        borderRadius: '14px',
        p: '16px 16px 14px',
        boxShadow: '0 3px 12px rgba(15,30,61,.06)',
        '&:before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: '5px', background: accent },
      }}
    >
      <Typography sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '.5px', fontSize: '10.5px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color: NAVY, fontSize: '23px', fontWeight: 700, lineHeight: 1.15, mt: '2px' }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ color: '#8a97a8', fontSize: '11px', mt: '2px' }}>{sub}</Typography> : null}
    </Box>
  );
}

/** PCPM/TM pill (reference .pcpm-pill). */
function PcpmPill({ pcpm, idx }) {
  const tone = PILL[idxTone(idx)];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: '8px',
        py: '2px',
        borderRadius: '999px',
        fontWeight: 600,
        fontSize: '11.5px',
        bgcolor: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.border}`,
      }}
    >
      {Number.isFinite(Number(pcpm)) ? formatValueAuto(pcpm) : '—'}
    </Box>
  );
}

/** One Top/Bottom table (reference hqTable), with its own client-side pager. */
function HqTable({ caption, rows, resetKey }) {
  const paged = usePagedRows(rows, { resetKey });
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: '12px', color: MUTED, mb: '6px' }}>{caption}</Typography>
      <TableContainer sx={{ maxHeight: 420, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_SX}>#</TableCell>
              <TableCell sx={HEAD_SX}>State</TableCell>
              <TableCell sx={HEAD_SX}>HQ</TableCell>
              <TableCell sx={HEAD_SX} align="right">TMs</TableCell>
              <TableCell sx={HEAD_SX} align="right">LY Sales</TableCell>
              <TableCell sx={HEAD_SX} align="right">Sales</TableCell>
              <TableCell sx={HEAD_SX} align="right">Target</TableCell>
              <TableCell sx={HEAD_SX} align="right">Ach %</TableCell>
              <TableCell sx={HEAD_SX} align="right">YoY %</TableCell>
              <TableCell sx={HEAD_SX} align="right">PCPM/TM</TableCell>
              <TableCell sx={HEAD_SX} align="right">vs Div (TM)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.pageRows.map((h, i) => (
              <TableRow key={`${h.hq}-${i}`} hover>
                <TableCell sx={CELL_SX}>{paged.offset + i + 1}</TableCell>
                <TableCell sx={CELL_SX}>{h.state ?? '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{h.hq ?? '—'}</TableCell>
                <TableCell sx={CELL_SX} align="right">{formatIndianInt(h.tm)}</TableCell>
                <TableCell sx={CELL_SX} align="right">{money(h.ly)}</TableCell>
                <TableCell sx={CELL_SX} align="right">{money(h.sales)}</TableCell>
                <TableCell sx={CELL_SX} align="right">{money(h.target)}</TableCell>
                <TableCell sx={CELL_SX} align="right">
                  <Box component="span" sx={{ color: DELTA_COLOR[achDelta(h.ach)] }}>
                    {Number.isFinite(Number(h.ach)) ? `${Number(h.ach).toFixed(1)}%` : '—'}
                  </Box>
                </TableCell>
                <TableCell sx={CELL_SX} align="right">
                  <Box component="span" sx={{ color: DELTA_COLOR[deltaClass(h.yoy)] }}>
                    {Number.isFinite(Number(h.yoy)) ? `${Number(h.yoy) > 0 ? '+' : ''}${Number(h.yoy).toFixed(1)}%` : '—'}
                  </Box>
                </TableCell>
                <TableCell sx={CELL_SX} align="right"><PcpmPill pcpm={h.pcpm_tm} idx={h.vs_div} /></TableCell>
                <TableCell sx={CELL_SX} align="right">
                  {Number.isFinite(Number(h.vs_div)) ? `${Number(h.vs_div).toFixed(0)}%` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <FieldSalesPager page={paged.page} pageCount={paged.pageCount} total={paged.total} onChange={paged.setPage} unit="HQs" />
    </Box>
  );
}

/**
 * HQ Productivity tab (reference "HQ Productivity"). Four summary cards with a
 * thin coloured top line, then Top N and Bottom N tables with the reference
 * columns (#, State, HQ, TMs, LY Sales, Sales, Target, Ach %, YoY %, PCPM/TM,
 * vs Div (TM)). The Top/Bottom dropdown (10/20/30/50/100) sends `n`. Subheading
 * "{Mon} — Top N & Bottom N by PCPM/TM". Values in Lakhs → ₹ Cr/L.
 *
 * @param {{ mon: string }} props  mon = the resolved {Mon} token
 */
export default function FieldSalesHqProductivityTab({ mon }) {
  const [n, setN] = useState(20);
  const query = useFieldSalesHqProductivity(n);
  const data = query.data;
  const cards = data?.cards ?? {};
  const top = data?.top ?? [];
  const bottom = data?.bottom ?? [];

  const loading = query.isLoading || query.isFetching;
  const anyData = top.length > 0 || bottom.length > 0;

  const pcpmRange =
    Number.isFinite(Number(cards.pcpm_tm_min)) && Number.isFinite(Number(cards.pcpm_tm_max))
      ? `${formatValueAuto(cards.pcpm_tm_min)} – ${formatValueAuto(cards.pcpm_tm_max)}`
      : '—';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      {/* Heading + Top/Bottom dropdown */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', mb: '4px' }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>👥 HQ Productivity</Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: MUTED, fontWeight: 600 }}>Show Top/Bottom</Typography>
          <Select
            size="small"
            value={n}
            onChange={(e) => setN(Number(e.target.value))}
            sx={{ fontSize: '12px', '& .MuiSelect-select': { py: '3px', px: '8px' } }}
          >
            {N_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: '12px' }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '13px', color: MUTED, mb: '12px' }}>
        {mon} — Top {n} &amp; Bottom {n} by PCPM/TM
      </Typography>

      {query.isError ? (
        <Alert severity="error">Failed to load HQ productivity.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {/* Summary cards (thin coloured top line only). */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' },
              gap: '14px',
              mb: '14px',
            }}
          >
            <SummaryCard
              accent={CARD_ACCENTS[0]}
              label="HQs in scope"
              value={formatIndianInt(cards.hqs_in_scope)}
              sub="unique HQ per division"
            />
            <SummaryCard
              accent={CARD_ACCENTS[1]}
              label="Division PCPM / HQ"
              value={Number.isFinite(Number(cards.pcpm_per_hq)) ? formatValueAuto(cards.pcpm_per_hq) : '—'}
              sub="per HQ"
            />
            <SummaryCard
              accent={CARD_ACCENTS[2]}
              label="Division PCPM / TM"
              value={Number.isFinite(Number(cards.pcpm_per_tm)) ? formatValueAuto(cards.pcpm_per_tm) : '—'}
              sub="per territory manager"
            />
            <SummaryCard
              accent={CARD_ACCENTS[3]}
              label="PCPM/TM range"
              value={pcpmRange}
              sub="bottom → top TM (staffed HQs)"
            />
          </Box>

          {!anyData ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No HQ rows for the current filters.
            </Typography>
          ) : (
            <>
              <HqTable caption={`▲ Top ${n} — highest PCPM/TM`} rows={top} resetKey={`top:${n}:${top.length}`} />
              <HqTable caption={`▼ Bottom ${n} — lowest PCPM/TM`} rows={bottom} resetKey={`bottom:${n}:${bottom.length}`} />
            </>
          )}
        </>
      )}
    </Box>
  );
}
